import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { LegislativeAlertsProvider } from "@/components/LegislativeAlertsProvider";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Suspense, lazy } from "react";
import { PageSkeleton } from "@/components/PageSkeleton";
import { SkipToContent } from "@/components/SkipToContent";
import { BackToTop } from "@/components/BackToTop";
import './i18n';

// Critical path — eagerly loaded
import Index from "./pages/Index";
import AboutPage from "./pages/AboutPage";
import ConnectPage from "./pages/ConnectPage";
import AuthPage from "./pages/AuthPage";
import NotFound from "./pages/NotFound";

// Lazy-loaded — secondary pages
const MediaConversationsPage = lazy(() => import("./pages/MediaConversationsPage"));
const PhysicianGapArticle = lazy(() => import("./pages/articles/PhysicianGapArticle"));
const HiddenTaxArticle = lazy(() => import("./pages/articles/HiddenTaxArticle"));
const NetworkAdequacyArticle = lazy(() => import("./pages/articles/NetworkAdequacyArticle"));
const NeighborIslandSurchargeArticle = lazy(() => import("./pages/articles/NeighborIslandSurchargeArticle"));
const BillionQuestionArticle = lazy(() => import("./pages/articles/BillionQuestionArticle"));
const MergerMathArticle = lazy(() => import("./pages/articles/MergerMathArticle"));
const DirectoryIsLyingArticle = lazy(() => import("./pages/articles/DirectoryIsLyingArticle"));
const DoctorsShortArticle = lazy(() => import("./pages/articles/DoctorsShortArticle"));
const GeographyBillingCodeArticle = lazy(() => import("./pages/articles/GeographyBillingCodeArticle"));
const MedicaleseArticle = lazy(() => import("./pages/articles/MedicaleseArticle"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const MediaPage = lazy(() => import("./pages/MediaPage"));
const SpeakingPage = lazy(() => import("./pages/SpeakingPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const PressKitPage = lazy(() => import("./pages/PressKitPage"));
const ExecutiveDashboardPage = lazy(() => import("./pages/ExecutiveDashboardPage"));
const CorrectionsLogPage = lazy(() => import("./pages/CorrectionsLogPage"));
const FieldNotesPage = lazy(() => import("./pages/FieldNotesPage"));
const ResearchPage = lazy(() => import("./pages/ResearchPage"));
const RhtpPage = lazy(() => import("./pages/RhtpPage"));
const MaiKaWahaEpisodePage = lazy(() => import("./pages/MaiKaWahaEpisodePage"));
const UnsubscribePage = lazy(() => import("./pages/UnsubscribePage"));
const WorkPage = lazy(() => import("./pages/WorkPage"));

// Lazy-loaded — protected/admin pages
const SituationRoomPage = lazy(() => import("./pages/SituationRoomPage"));
const PlatformManualPage = lazy(() => import("./pages/PlatformManualPage"));
const FunderPortalPage = lazy(() => import("./pages/FunderPortalPage"));
const SavedBriefingsPage = lazy(() => import("./pages/SavedBriefingsPage"));
const AiBriefingPage = lazy(() => import("./pages/AiBriefingPage"));
const StakeholderCrmPage = lazy(() => import("./pages/StakeholderCrmPage"));
const NotebooksPage = lazy(() => import("./pages/NotebooksPage"));
const SpeechGeneratorPage = lazy(() => import("./pages/SpeechGeneratorPage"));
const InquiryDrafterPage = lazy(() => import("./pages/InquiryDrafterPage"));
const ZoomSchedulerPage = lazy(() => import("./components/ZoomSchedulerPage"));
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
const TestimonialsAdmin = lazy(() => import("./components/admin/TestimonialsAdmin"));
const CaseStudiesAdmin = lazy(() => import("./components/admin/CaseStudiesAdmin"));
const MediaAdmin = lazy(() => import("./components/admin/MediaAdmin"));
const StatsAdmin = lazy(() => import("./components/admin/StatsAdmin"));
const NewsletterAttemptsAdmin = lazy(() => import("./components/admin/NewsletterAttemptsAdmin"));
const ConsentVersionsAdmin = lazy(() => import("./components/admin/ConsentVersionsAdmin"));
const ConsentAuditAdmin = lazy(() => import("./components/admin/ConsentAuditAdmin"));
const FeaturedVideoAnalyticsAdmin = lazy(() => import("./components/admin/FeaturedVideoAnalyticsAdmin"));
const CmsAdmin = lazy(() => import("./components/admin/CmsAdmin"));
const SystemAdmin = lazy(() => import("./components/admin/SystemAdmin"));
const AdminFeedsPage = lazy(() => import("./pages/AdminFeedsPage"));
const FeedStatusPage = lazy(() => import("./pages/FeedStatusPage"));

const queryClient = new QueryClient();

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -4 }}
        transition={{ duration: 0.3, ease: [0.25, 0.4, 0.25, 1] }}
      >
        <Routes location={location}>
          {/* Critical path */}
          <Route path="/" element={<Index />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/connect" element={<ConnectPage />} />
          <Route path="/manifesto" element={<Navigate to="/articles" replace />} />
          <Route path="/auth" element={<AuthPage />} />

          {/* Public — lazy loaded */}
          <Route path="/expertise" element={<Navigate to="/about" replace />} />
          <Route path="/impact" element={<Navigate to="/about" replace />} />
          <Route path="/work" element={<WorkPage />} />
          <Route path="/media" element={<MediaConversationsPage />} />
          <Route path="/media/mai-ka-waha/ep1" element={<MaiKaWahaEpisodePage />} />
          <Route path="/briefings" element={<Navigate to="/articles" replace />} />
          <Route path="/briefings/:slug" element={<Navigate to="/articles" replace />} />
          <Route path="/policy" element={<Navigate to="/rhtp" replace />} />
          <Route path="/partners" element={<Navigate to="/about" replace />} />
          <Route path="/dashboard" element={<ExecutiveDashboardPage />} />
          <Route path="/corrections" element={<CorrectionsLogPage />} />
          <Route path="/rhtp" element={<RhtpPage />} />
          <Route path="/field-notes" element={<FieldNotesPage />} />
          <Route path="/research" element={<ResearchPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/impact-report" element={<Navigate to="/about" replace />} />
          <Route path="/case-studies" element={<Navigate to="/about" replace />} />
          <Route path="/case-studies/:slug" element={<Navigate to="/about" replace />} />
          <Route path="/media/:slug" element={<MediaPage />} />
          <Route path="/testimonials" element={<Navigate to="/about" replace />} />
          <Route path="/speaking" element={<SpeakingPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/press-kit" element={<PressKitPage />} />
          <Route path="/press" element={<Navigate to="/press-kit" replace />} />
          <Route path="/nitta-v-hmsa" element={<Navigate to="/articles" replace />} />
          <Route path="/get-calculator" element={<Navigate to="/dashboard" replace />} />
          <Route path="/legislative" element={<Navigate to="/articles" replace />} />
          <Route path="/demo" element={<Navigate to="/about" replace />} />
          <Route path="/strategic-partnerships" element={<Navigate to="/about" replace />} />
          <Route path="/influence" element={<Navigate to="/about" replace />} />
          <Route path="/pitch" element={<Navigate to="/about" replace />} />
          <Route path="/intelligence" element={<Navigate to="/articles" replace />} />
          <Route path="/for-legislators" element={<Navigate to="/about" replace />} />
          <Route path="/for-media" element={<Navigate to="/press-kit" replace />} />
          <Route path="/for-health-systems" element={<Navigate to="/about" replace />} />
          <Route path="/for-funders" element={<Navigate to="/about" replace />} />
          <Route path="/briefing-generator" element={<Navigate to="/about" replace />} />
          <Route path="/articles" element={<ResearchPage />} />
          <Route path="/articles/776-physician-gap" element={<PhysicianGapArticle />} />
          <Route path="/articles/hidden-tax-hawaii-medicine" element={<HiddenTaxArticle />} />
          <Route path="/articles/network-adequacy-theater" element={<NetworkAdequacyArticle />} />
          <Route path="/articles/neighbor-island-surcharge" element={<NeighborIslandSurchargeArticle />} />
          <Route path="/articles/2-3-billion-question" element={<BillionQuestionArticle />} />
          <Route path="/articles/merger-math" element={<MergerMathArticle />} />
          <Route path="/articles/directory-is-lying" element={<DirectoryIsLyingArticle />} />
          <Route path="/articles/644-doctors-short" element={<DoctorsShortArticle />} />
          <Route path="/articles/geography-not-billing-code" element={<GeographyBillingCodeArticle />} />
          <Route path="/articles/i-dont-speak-medicalese" element={<MedicaleseArticle />} />
          <Route path="/op-eds" element={<Navigate to="/articles" replace />} />
          <Route path="/methodology" element={<Navigate to="/dashboard" replace />} />
          <Route path="/unsubscribe" element={<UnsubscribePage />} />
          <Route path="/logo-concepts" element={<Navigate to="/about" replace />} />
          <Route path="/now" element={<Navigate to="/about" replace />} />

          {/* Protected Routes */}
          <Route path="/app/situation-room" element={<ProtectedRoute><SituationRoomPage /></ProtectedRoute>} />
          <Route path="/app/manual" element={<ProtectedRoute><PlatformManualPage /></ProtectedRoute>} />
          <Route path="/app/funder-portal" element={<ProtectedRoute><FunderPortalPage /></ProtectedRoute>} />
          <Route path="/app/briefings" element={<ProtectedRoute><SavedBriefingsPage /></ProtectedRoute>} />
          <Route path="/app/ai-briefing" element={<ProtectedRoute><AiBriefingPage /></ProtectedRoute>} />
          <Route path="/app/crm" element={<ProtectedRoute><StakeholderCrmPage /></ProtectedRoute>} />
          <Route path="/app/notebooks" element={<ProtectedRoute><NotebooksPage /></ProtectedRoute>} />
          <Route path="/app/speech-generator" element={<ProtectedRoute><SpeechGeneratorPage /></ProtectedRoute>} />
          <Route path="/app/inquiry-drafter" element={<ProtectedRoute><InquiryDrafterPage /></ProtectedRoute>} />

          {/* Admin Routes */}
          <Route path="/admin/feeds" element={<AdminFeedsPage />} />
          <Route path="/feed-status" element={<FeedStatusPage />} />
          <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
            <Route path="testimonials" element={<TestimonialsAdmin />} />
            <Route path="case-studies" element={<CaseStudiesAdmin />} />
            <Route path="media" element={<MediaAdmin />} />
            <Route path="stats" element={<StatsAdmin />} />
            <Route path="scheduler" element={<ZoomSchedulerPage />} />
            <Route path="newsletter-log" element={<NewsletterAttemptsAdmin />} />
            <Route path="consent-versions" element={<ConsentVersionsAdmin />} />
            <Route path="consent-audit" element={<ConsentAuditAdmin />} />
            <Route path="video-analytics" element={<FeaturedVideoAnalyticsAdmin />} />
            <Route path="cms" element={<CmsAdmin />} />
            <Route path="system" element={<SystemAdmin />} />
          </Route>

          {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <SkipToContent />
          <LegislativeAlertsProvider />
          <ErrorBoundary>
          <main id="main-content">
          <Suspense fallback={<PageSkeleton />}>
            <AnimatedRoutes />
          </Suspense>
          </main>
          <BackToTop />
          </ErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </HelmetProvider>
  </QueryClientProvider>
);

export default App;
